import Compressor from 'compressorjs'
import {
    ACCEPTED_FILE_TYPES,
    DOC_MAX_SIZE,
    IMAGE_FILE_COMPRESSION,
    IMAGE_FILE_QUALITY,
    PDF_MIME_TYPE,
    STATUS_LIST,
    UNSUPPORTED_FILE_TYPE_ERROR,
    FILE_SIZE_ERROR
} from 'constants/docUploadConstant'
import { getDocNameWithActor, useAddDocData, useGetDocData } from 'hooks/useDocListData'
import { useDocListStore } from 'store/useDocListStore'
import { useDocPreviewStore } from 'store/useDocPreviewStore'
import { useNotificationStore } from 'store/useNotificationStore'
import { docUploadAnalytics } from 'analytics/docUploadAnalytics'

const { ERROR } = STATUS_LIST

const convertFileToBase64 = (file, docInfo, setPreview) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = function () {
        setPreview({
            ...docInfo,
            base64Data: reader.result.split(',')[1],
            mimeType: file.type,
            fileSize: file.size
        })
    }
    reader.onerror = function (error) {
        console.log('Error: ', error)
    }
}

const handleCompression = (file, docInfo, setPreview) => {
    if (IMAGE_FILE_COMPRESSION && file.type !== PDF_MIME_TYPE) {
        new Compressor(file, {
            quality: IMAGE_FILE_QUALITY,
            success: (compressedResult) => {
                convertFileToBase64(compressedResult, docInfo, setPreview)
            }
        })
    } else {
        convertFileToBase64(file, docInfo, setPreview)
    }
}

const SIDES_PREVIEW_DOCUMENT_TYPES = ['DRIVERSLICENSE']

export const isSidesPreview = (documentType) => SIDES_PREVIEW_DOCUMENT_TYPES.includes(documentType)

export const useDocPreview = () => {
    const {
        isDialogOpen,
        preview,
        backSidePreview,
        isLoading,
        loadingProgress,
        editPreview,
        isEditPreview,
        setPreview,
        closeDialog,
        toggleIsDialogOpen,
        setBackSidePreview,
        isSidesPreview,
        setIsSidesPreview,
        setEditPreview,
        clearPreview,
        clearBackSidePreview,
        setIsLoading,
        setLoadingProgress,
        clearEditPreview
    } = useDocPreviewStore((state) => state)
    const { updateDoc } = useDocListStore((state) => state)
    const { showFileSizeNotification, showUnsupportedFileNotification } = useNotificationStore((state) => state)
    const { mutateAsync: addDoc } = useAddDocData()
    const { mutateAsync: getDoc } = useGetDocData()

    const setPreviewBeforeUpload = (event, docInfo, setPreview) => {
        const [file] = event.target.files
        setIsLoading(true)
        setLoadingProgress(0)
        handleCompression(file, docInfo, setPreview)
    }

    const setPreviewAfterUpload = async (docInfo) => {
        if (!docInfo?.base64Data) {
            setIsLoading(true)
            setLoadingProgress(0)
            const res = await getDoc(docInfo) // do a get document call if base64Data is not there
            setPreview({
                ...docInfo,
                base64Data: res?.data?.data,
                mimeType: PDF_MIME_TYPE
            })
        } else {
            setPreview(docInfo)
        }
    }

    const isUnsupportedFileType = (event, acceptedFileTypes = ACCEPTED_FILE_TYPES) => {
        const path = (event?.target?.value || '').split('.')
        const extension = `.${path[path.length - 1]}`.toLowerCase()
        return !acceptedFileTypes.includes(extension)
    }

    const handleSidesPreview = (event, docInfo, setPreview) => {
        if (isUnsupportedFileType(event)) {
            showUnsupportedFileNotification()
            docUploadAnalytics.track('error', UNSUPPORTED_FILE_TYPE_ERROR)
        } else {
            setPreviewBeforeUpload(event, docInfo, setPreview)
        }
    }

    const handleToggleDialog = async (event, docInfo) => {
        setIsSidesPreview(false)
        if (event?.target?.files?.length === 0) {
            closeDialog()
        } else if (isUnsupportedFileType(event)) {
            showUnsupportedFileNotification()
            docUploadAnalytics.track('error', UNSUPPORTED_FILE_TYPE_ERROR)
            updateDoc(docInfo.id, {
                status: ERROR,
                mimeType: '',
                base64Data: ''
            })
        } else {
            toggleIsDialogOpen()
            if (event?.target?.files?.[0]) {
                setPreviewBeforeUpload(event, docInfo, setPreview)
            } else if (docInfo?.documentId) {
                await setPreviewAfterUpload(docInfo)
            }
        }
    }

    const openSidesUpload = async (docInfo) => {
        setIsSidesPreview(true)
        toggleIsDialogOpen()
        setPreview(docInfo)
        setBackSidePreview(docInfo)
    }

    const handleDocUpload = () => {
        docUploadAnalytics.track('uploadDoc', getDocNameWithActor(preview.documentName, preview.actorRole))
        closeDialog()
        const totalFileSize = preview.fileSize + backSidePreview.fileSize
        if (totalFileSize > DOC_MAX_SIZE) {
            // if file size is more than 5MB then we don't need to call upload api
            docUploadAnalytics.track('error', FILE_SIZE_ERROR)
            showFileSizeNotification()
            updateDoc(preview.id, {
                progressCount: 0,
                status: ERROR,
                mimeType: '',
                base64Data: ''
            })
        } else {
            addDoc({ preview, backSidePreview })
        }
    }

    const openEditPreview = (preview, back) => {
        preview = { ...preview, back }
        setEditPreview(preview)
        const { documentName, actorRole } = preview
        docUploadAnalytics.track('edit', getDocNameWithActor(documentName, actorRole))
    }

    const closeEditPreview = (preview) => {
        if (preview?.back) {
            const { documentName, actorRole } = preview
            docUploadAnalytics.track('done', getDocNameWithActor(documentName, actorRole))
            setBackSidePreview(preview)
        } else {
            setPreview(preview)
            const { documentName, actorRole } = preview
            docUploadAnalytics.track('done', getDocNameWithActor(documentName, actorRole))
        }
        clearEditPreview()
    }

    const handleReuploadFile = (event) => {
        clearEditPreview()
        const [file] = event.target.files
        handleCompression(file, editPreview, setEditPreview)
    }

    const onKeyDownHandler = (event, func) => {
        if (event.keyCode === 13) {
            func()
        }
    }

    const onClearPreview = () => {
        const { documentName, actorRole } = preview
        clearPreview()
        docUploadAnalytics.track('delete', getDocNameWithActor(documentName, actorRole))
    }

    const onClearBackSidePreview = () => {
        const { documentName, actorRole } = backSidePreview
        clearBackSidePreview()
        docUploadAnalytics.track('delete', getDocNameWithActor(documentName, actorRole))
    }

    return {
        preview,
        backSidePreview,
        editPreview,
        isDialogOpen,
        isSidesPreview,
        isEditPreview,
        closeDialog,
        clearPreview,
        clearBackSidePreview,
        isLoading,
        loadingProgress,
        clearEditPreview,
        setIsLoading,
        setLoadingProgress,
        setPreview,
        setBackSidePreview,
        setEditPreview,
        handleToggleDialog,
        handleDocUpload,
        setPreviewBeforeUpload,
        openSidesUpload,
        openEditPreview,
        closeEditPreview,
        handleReuploadFile,
        handleSidesPreview,
        onKeyDownHandler,
        isUnsupportedFileType,
        onClearPreview,
        onClearBackSidePreview
    }
}
